/**
 * The outcome of testing a connection, under its form: testing, connected (with
 * what the platform said, e.g. "Signed in to qFlow workspace Radiology"), or
 * failed with the platform's reason and what to do next.
 */

import { CircleAlert, CircleCheck } from "lucide-react";
import type { Connection } from "./types";

export interface CheckOutcome {
  ok: boolean;
  message: string;
}

/** A saved connection's last failed test, so an edit opened to fix it says why. */
export const lastCheck = (c: Connection | undefined): CheckOutcome | null =>
  c?.last_checked_at && c.last_check_error ? { ok: false, message: c.last_check_error } : null;

export function CheckResult({ checking, result }: { checking: boolean; result: CheckOutcome | null }) {
  return (
    <div aria-live="polite">
      {checking ? (
        <p className="text-xs text-muted">Testing the connection…</p>
      ) : result?.ok ? (
        <p className="flex items-start gap-2 rounded-[9px] bg-success-bg px-3 py-2 text-xs text-success">
          <CircleCheck size={14} className="mt-px shrink-0" aria-hidden />
          <span>
            <strong>Connected.</strong> {result.message}
          </span>
        </p>
      ) : result ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-[9px] bg-err-bg px-3 py-2 text-xs text-err"
        >
          <CircleAlert size={14} className="mt-px shrink-0" aria-hidden />
          <span className="flex flex-col gap-0.5">
            <span>
              <strong>Could not connect.</strong> {result.message}
            </span>
            <span>Fix the fields above and save again.</span>
          </span>
        </div>
      ) : null}
    </div>
  );
}
